import { type FormEvent, useState } from 'react';

import { errorMessage } from '@/shared/api/errors';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { PasswordInput } from '@/shared/ui/PasswordInput';
import { Spinner } from '@/shared/ui/Spinner';
import { formatDate } from '@/shared/utils/format';

import { RecoveryCodesPanel, SetupPanel } from './SecondFactor';
import type { TrustedDevice } from './types';
import { useForgetDevice, useRenewRecoveryCodes, useTwoFactorStatus } from './useTwoFactor';

const dayFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium' });

/** Few enough recovery codes left to say so. */
const FEW_CODES = 3;

export type TwoFactorAvailability = 'required' | 'optional' | 'unavailable';

/** What the card shows below its title: the state, or one of the three things to do. */
type Panel =
  { kind: 'idle' } | { kind: 'setup' } | { kind: 'renew' } | { kind: 'codes'; codes: string[] };

/**
 * The profile's «Doble factor» (contract v10.45, ADR 0039 of the back): on or off and
 * since when, the recovery codes left, the remembered devices. «Activar» reuses the
 * setup of the door; «Códigos de recuperación nuevos» asks for the password and shows
 * the ten new codes the same way. There is no «Desactivar»: the API has none, and losing
 * the phone is what an administrator's «Reiniciar doble factor» is for.
 *
 * Without the server's key (`unavailable`) the card says it in one line and asks nothing.
 */
export function TwoFactorCard({
  account,
  availability,
}: {
  account: string;
  availability: TwoFactorAvailability;
}) {
  const unavailable = availability === 'unavailable';
  const status = useTwoFactorStatus(!unavailable);
  const [panel, setPanel] = useState<Panel>({ kind: 'idle' });
  const idle = () => setPanel({ kind: 'idle' });

  return (
    <Card
      title="Doble factor"
      description="Además de la contraseña, un código de seis cifras de una aplicación del móvil."
      className="max-w-lg"
    >
      {unavailable || status.data?.availability === 'unavailable' ? (
        <p className="text-ink-2 text-[14px]">
          El doble factor todavía no está disponible en esta instalación.
        </p>
      ) : panel.kind === 'codes' ? (
        <RecoveryCodesPanel codes={panel.codes} account={account} doneLabel="Hecho" onDone={idle} />
      ) : panel.kind === 'setup' ? (
        <SetupPanel
          submitLabel="Activar"
          askPassword
          onEnabled={(result) => setPanel({ kind: 'codes', codes: result.recovery_codes })}
          onCancel={idle}
        />
      ) : panel.kind === 'renew' ? (
        <RenewCodes onRenewed={(codes) => setPanel({ kind: 'codes', codes })} onCancel={idle} />
      ) : status.isPending ? (
        <Spinner variant="fill" label="Cargando el doble factor" />
      ) : status.isError ? (
        <ErrorMessage error={status.error}>No se pudo cargar el doble factor</ErrorMessage>
      ) : status.data.enabled ? (
        <div className="flex flex-col gap-4">
          <div className="text-ink-2 flex flex-col gap-1 text-[14px]">
            <p>
              <Badge tone="ok">Activado</Badge>
              {status.data.enabled_at
                ? ` desde el ${formatDate(dayFormat, status.data.enabled_at)}.`
                : null}
            </p>
            <p className={(status.data.recovery_codes_left ?? 0) <= FEW_CODES ? 'text-warn' : ''}>
              {codesLeft(status.data.recovery_codes_left ?? 0)}
            </p>
          </div>
          <div>
            <Button variant="secondary" onClick={() => setPanel({ kind: 'renew' })}>
              Códigos de recuperación nuevos
            </Button>
          </div>
          <Devices devices={status.data.devices ?? []} />
          <p className="text-ink-3 text-[12.5px] leading-snug">
            Si pierdes el móvil y los códigos, quien administra la aplicación te lo reinicia y lo
            vuelves a activar {availability === 'required' ? 'al entrar' : 'desde aquí'}.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-ink-2 text-[14px]">
            Sin activar. Con él, quien sepa tu contraseña no puede entrar sin tu móvil.
          </p>
          <div>
            <Button onClick={() => setPanel({ kind: 'setup' })}>Activar</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function codesLeft(left: number): string {
  if (left === 0) return 'No te queda ningún código de recuperación: pide nuevos.';
  const count = `Te ${left === 1 ? 'queda 1 código' : `quedan ${left} códigos`} de recuperación.`;
  return left <= FEW_CODES ? `${count} Pide nuevos antes de que se acaben.` : count;
}

/** New recovery codes ask for the password again, like a password change. */
function RenewCodes({
  onRenewed,
  onCancel,
}: {
  onRenewed: (codes: string[]) => void;
  onCancel: () => void;
}) {
  const renew = useRenewRecoveryCodes();
  const [password, setPassword] = useState('');

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!password || renew.isPending) return;
    renew.mutate(password, { onSuccess: (result) => onRenewed(result.recovery_codes) });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <PasswordInput
        label="Tu contraseña"
        autoComplete="current-password"
        autoFocus
        value={password}
        onChange={(event) => {
          setPassword(event.target.value);
          if (renew.isError) renew.reset();
        }}
        hint="Los códigos que tienes ahora dejarán de valer, los hayas usado o no."
        error={renew.isError ? errorMessage(renew.error) : undefined}
        required
      />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={renew.isPending || password.length < 8}>
          {renew.isPending ? 'Generando…' : 'Pedir códigos nuevos'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={renew.isPending}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

/** The browsers that skip the code for 30 days, each with «Olvidar». */
function Devices({ devices }: { devices: TrustedDevice[] }) {
  const forget = useForgetDevice();

  return (
    <section aria-labelledby="remembered-devices" className="flex flex-col gap-1.5">
      <h3 id="remembered-devices" className="text-ink text-[14px] font-semibold">
        Dispositivos recordados
      </h3>
      {devices.length === 0 ? (
        <p className="text-ink-3 text-[13.5px] leading-snug">
          Ninguno. Al entrar puedes marcar «Recordar este dispositivo» para que ese navegador no te
          pida el código durante 30 días.
        </p>
      ) : (
        <ul className="divide-line-2 border-line-2 divide-y border-y">
          {devices.map((device) => (
            <li key={device.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <p className="text-ink flex flex-wrap items-center gap-2 text-[14px] font-medium">
                  <span className="break-words">{device.label}</span>
                  {device.current ? <Badge tone="info">Este navegador</Badge> : null}
                </p>
                <p className="text-ink-3 text-[12.5px] leading-snug">{deviceLine(device)}</p>
              </div>
              <Button
                size="sm"
                variant="secondary"
                aria-label={`Olvidar ${device.label}${device.current ? ' (este navegador)' : ''}`}
                disabled={forget.isPending}
                onClick={() => forget.mutate(device.id)}
              >
                Olvidar
              </Button>
            </li>
          ))}
        </ul>
      )}
      {forget.isError ? (
        <ErrorMessage error={forget.error}>No se pudo olvidar el dispositivo</ErrorMessage>
      ) : null}
    </section>
  );
}

function deviceLine(device: TrustedDevice): string {
  return [
    `Recordado el ${formatDate(dayFormat, device.created_at)}`,
    device.last_used_at
      ? `usado por última vez el ${formatDate(dayFormat, device.last_used_at)}`
      : null,
    `hasta el ${formatDate(dayFormat, device.expires_at)}`,
  ]
    .filter(Boolean)
    .join(' · ');
}
